import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AppShell from './shell/AppShell';
import PipelineBoard from './PipelineBoard';
import SearchOutreachTab from './SearchOutreachTab';
import CandidateTable from './CandidateTable';
import StatusReport from './StatusReport';
import Tooltip from './Tooltip';
import AddToCampaignSidebar from './AddToCampaignSidebar';
import ClientAlertsToggle from './ClientAlertsToggle';
import EditSearchDrawer from './EditSearchDrawer';
import ResearchTab from './ResearchTab';
import FilterSelect from './shell/FilterSelect';
import BulkActionBar from './shell/BulkActionBar';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

// Pool status definitions (matches backend)
const POOL_STATUSES = [
  { key: 'research', label: 'Research', category: 'active', color: '#6b7280' },
  { key: 'pursuing', label: 'Pursuing', category: 'active', color: '#3b82f6' },
  { key: 'qualifying', label: 'Qualifying', category: 'active', color: '#6366f1' },
  { key: 'potential_candidate', label: 'Potential Candidate', category: 'active', color: '#06b6d4' },
  { key: 'interviewing', label: 'Interviewing', category: 'active', color: '#f59e0b' },
  { key: 'finalist', label: 'Finalist', category: 'active', color: '#10b981' },
  { key: 'offer_extended', label: 'Offer Extended', category: 'active', color: '#059669' },
  { key: 'placed', label: 'Placed', category: 'terminal', color: '#047857' },
  { key: 'on_hold', label: 'On Hold', category: 'paused', color: '#eab308' },
  { key: 'referral_source', label: 'Referral Source', category: 'info', color: '#8b5cf6' },
  { key: 'not_interested', label: 'Not Interested', category: 'terminal', color: '#9ca3af' },
  { key: 'location_issue', label: 'Location Issue', category: 'terminal', color: '#f97316' },
  { key: 'turned_off', label: 'Turned Off', category: 'terminal', color: '#ef4444' },
  { key: 'disqualified', label: 'Disqualified', category: 'terminal', color: '#dc2626' },
  { key: 'dq_by_client', label: 'DQ by Client', category: 'terminal', color: '#991b1b' },
  { key: 'withdrew', label: 'Withdrew', category: 'terminal', color: '#64748b' },
];

const TERMINAL_STATUSES = POOL_STATUSES.filter(s => s.category === 'terminal').map(s => s.key);
const STATUS_MAP = Object.fromEntries(POOL_STATUSES.map(s => [s.key, s]));

const PRESET_COLORS = [
  { key: 'red', hex: '#ef4444' },
  { key: 'orange', hex: '#f97316' },
  { key: 'yellow', hex: '#eab308' },
  { key: 'green', hex: '#22c55e' },
  { key: 'blue', hex: '#3b82f6' },
  { key: 'purple', hex: '#8b5cf6' },
  { key: 'pink', hex: '#ec4899' },
  { key: 'gray', hex: '#6b7280' },
];

const COLOR_MAP = Object.fromEntries(PRESET_COLORS.map(c => [c.key, c.hex]));

const SearchDetailPage = () => {
  const { id } = useParams();
  const pipelineId = parseInt(id);
  const [searchParams, setSearchParams] = useSearchParams();

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'pool');
  const [pipelineSubTab, setPipelineSubTab] = useState(searchParams.get('sub') || 'kanban');

  // Sync tab state -> URL so refresh / back-button preserve the active tab.
  // Use `replace: true` so we don't pollute history with every tab click.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', activeTab);
    if (activeTab === 'pipeline') next.set('sub', pipelineSubTab); else next.delete('sub');
    // Only write if something actually changed (avoid loop)
    const curTab = searchParams.get('tab');
    const curSub = searchParams.get('sub');
    if (curTab !== activeTab || (activeTab === 'pipeline' && curSub !== pipelineSubTab)) {
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, pipelineSubTab, searchParams, setSearchParams]);
  const [showRequirements, setShowRequirements] = useState(false);
  const [pipeline, setPipeline] = useState(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [mappingBulk, setMappingBulk] = useState(null);
  const [loading, setLoading] = useState(true);

  // Candidates tab state
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [counts, setCounts] = useState({ total: 0, on_pipeline: 0 });
  const [statusCounts, setStatusCounts] = useState({});

  // Pool filters
  const [poolStatusFilter, setPoolStatusFilter] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const [currentFilters, setCurrentFilters] = useState({});

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [clearTrigger, setClearTrigger] = useState(0);

  // Inline dropdowns
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null); // candidateId
  const [openColorPicker, setOpenColorPicker] = useState(null); // candidateId

  // Add candidates modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const addSearchRef = useRef(null);

  // Campaign enrollment modal
  // Add to Campaign — sidebar delegated to AddToCampaignSidebar. When
  // this is a non-null array the sidebar opens with those candidate IDs
  // (single row or bulk selection). Closing the sidebar sets it back to
  // null. All the multi-step logic (pick / customize / contacts) lives
  // in the shared component.
  const [campaignSidebarCandidates, setCampaignSidebarCandidates] = useState(null);

  // Load pipeline info
  useEffect(() => {
    const loadPipeline = async () => {
      try {
        const resp = await fetch(`${API}/api/pipelines/${pipelineId}`, { headers });
        if (resp.ok) setPipeline(await resp.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadPipeline();
  }, [pipelineId]);

  // Load search candidates
  const loadCandidates = useCallback(async (filters) => {
    setCandidatesLoading(true);
    try {
      const params = new URLSearchParams();
      const mergedFilters = { ...filters };
      // Apply pool-level filters
      if (poolStatusFilter) mergedFilters.pool_status = poolStatusFilter;
      if (colorFilter) mergedFilters.color_filter = colorFilter;
      
      Object.entries(mergedFilters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) params.set(key, value);
      });
      setCurrentFilters(mergedFilters);
      
      const resp = await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates?${params}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        const normalized = (data.candidates || []).map(c => ({
          ...c,
          profile_picture_url: c.ccd_profile_picture || c.profile_picture_url,
          sent_at: c.added_at,
          updated_at: c.added_at,
          pipeline_names: c.on_pipeline ? [c.pipeline_stage_name || 'On Pipeline'] : [],
          client_stage_names: c.on_pipeline ? [c.pipeline_stage_name] : [],
          has_placed: false,
          has_interviewing: c.on_pipeline,
          display_location: c.client_location || c.location,
          search_status: c.search_status,
          on_pipeline: c.on_pipeline,
        }));
        const filtered = normalized;
        setCandidates(filtered);
        setCounts({ total: data.total || normalized.length, on_pipeline: data.on_pipeline || 0 });
        setStatusCounts(data.status_counts || {});
      }
    } catch (e) { console.error(e); }
    finally { setCandidatesLoading(false); }
  }, [pipelineId, poolStatusFilter, colorFilter]);

  useEffect(() => {
    if (activeTab === 'pool') loadCandidates({});
  }, [activeTab, loadCandidates]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.pool-status-dropdown') && !e.target.closest('.pool-color-picker')) {
        setOpenStatusDropdown(null);
        setOpenColorPicker(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Add candidates search
  useEffect(() => {
    if (!addSearch || addSearch.length < 2) { setAddResults([]); return; }
    const timer = setTimeout(async () => {
      setAddSearching(true);
      try {
        const resp = await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates/available?search=${encodeURIComponent(addSearch)}`, { headers });
        if (resp.ok) setAddResults(await resp.json());
      } catch (e) { console.error(e); }
      finally { setAddSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [addSearch]);

  // === ACTIONS ===

  const addToSearch = async (candidateIds) => {
    try {
      await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates`, { method: 'POST', headers, body: JSON.stringify({ candidate_ids: candidateIds }) });
      loadCandidates(currentFilters);
    } catch (e) { console.error(e); }
  };

  const removeFromSearch = async (candidateId) => {
    if (!window.confirm('Remove this candidate from the search pool?')) return;
    try {
      await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates/${candidateId}`, { method: 'DELETE', headers });
      loadCandidates(currentFilters);
    } catch (e) { console.error(e); }
  };

  const changePoolStatus = async (candidateId, newStatus) => {
    try {
      await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates/${candidateId}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ pool_status: newStatus })
      });
      setOpenStatusDropdown(null);
      loadCandidates(currentFilters);
    } catch (e) { console.error(e); }
  };

  const changeColor = async (candidateId, newColor) => {
    try {
      await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates/${candidateId}/color`, {
        method: 'PUT', headers, body: JSON.stringify({ color: newColor })
      });
      setOpenColorPicker(null);
      loadCandidates(currentFilters);
    } catch (e) { console.error(e); }
  };

  const bulkChangeStatus = async (newStatus) => {
    if (selectedIds.length === 0) return;
    try {
      await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates/bulk-status`, {
        method: 'PUT', headers, body: JSON.stringify({ candidate_ids: selectedIds, pool_status: newStatus })
      });
      loadCandidates(currentFilters);
    } catch (e) { console.error(e); }
  };

  const bulkChangeColor = async (newColor) => {
    if (selectedIds.length === 0) return;
    try {
      await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates/bulk-color`, {
        method: 'PUT', headers, body: JSON.stringify({ candidate_ids: selectedIds, color: newColor })
      });
      loadCandidates(currentFilters);
    } catch (e) { console.error(e); }
  };

  // Export the selected pool candidates to a CSV file (client-side).
  const exportSelectedCsv = () => {
    const rows = candidates.filter(c => selectedIds.includes(c.id));
    if (rows.length === 0) return;
    const cols = [
      ['name', 'Name'], ['email', 'Email'], ['phone', 'Phone'],
      ['current_title', 'Title'], ['current_company', 'Company'],
      ['pool_status', 'Status'], ['location', 'Location'], ['linkedin_url', 'LinkedIn'],
    ];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map(c => c[1]).join(',');
    const body = rows.map(r => cols.map(c => esc(r[c[0]])).join(',')).join('\n');
    const csv = header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(pipeline?.name || 'search').replace(/[^a-z0-9]+/gi, '_')}_pool_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const promoteToIPipeline = async (pipelineIdTarget, candidateIds) => {
    try {
      const resp = await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates/to-pipeline`, {
        method: 'POST', headers, body: JSON.stringify({ candidate_ids: candidateIds })
      });
      if (resp.ok) {
        const result = await resp.json();
        const msg = [];
        if (result.promoted > 0) msg.push(`${result.promoted} added to pipeline`);
        if (result.already_on_pipeline > 0) msg.push(`${result.already_on_pipeline} already on pipeline`);
        alert(msg.join(', '));
        loadCandidates(currentFilters);
      }
    } catch (e) { console.error(e); alert('Failed to add to pipeline'); }
  };

  const handlePromoteSingle = async (candidateId) => {
    try {
      const resp = await fetch(`${API}/api/pipelines/${pipelineId}/search-candidates/to-pipeline`, {
        method: 'POST', headers, body: JSON.stringify({ candidate_ids: [candidateId] })
      });
      if (resp.ok) {
        const result = await resp.json();
        if (result.promoted > 0) alert('Added to pipeline');
        else if (result.already_on_pipeline > 0) alert('Already on pipeline');
        loadCandidates(currentFilters);
      }
    } catch (e) { console.error(e); alert('Failed to add to pipeline'); }
  };

  // Add to Campaign — opens the shared AddToCampaignSidebar (see the
  // sidebar's state above). Single row uses [id]; bulk uses selectedIds.
  const openCampaignSidebar = (candidateIds) => {
    setCampaignSidebarCandidates(candidateIds);
  };

  // === STATUS PILL COMPONENT ===
  const StatusPill = ({ candidate }) => {
    const status = STATUS_MAP[candidate.pool_status] || STATUS_MAP['research'];
    const isTerminal = TERMINAL_STATUSES.includes(candidate.pool_status);
    const isOpen = openStatusDropdown === candidate.candidate_id;

    return (
      <div className="relative pool-status-dropdown">
        <button
          onClick={(e) => { e.stopPropagation(); setOpenStatusDropdown(isOpen ? null : candidate.candidate_id); setOpenColorPicker(null); }}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors hover:ring-1 hover:ring-offset-1 ${isTerminal ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
        >
          {status.label}
          <svg className="w-2.5 h-2.5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {isOpen && (
          <div className="absolute z-50 left-0 top-full mt-1 bg-white border rounded-lg shadow-xl py-1 w-48 max-h-72 overflow-auto">
            {['active', 'paused', 'info', 'terminal'].map(category => {
              const items = POOL_STATUSES.filter(s => s.category === category);
              return (
                <div key={category}>
                  <div className="px-2 py-1 text-[9px] font-semibold text-gray-400 uppercase tracking-wider">{category}</div>
                  {items.map(s => (
                    <button key={s.key} onClick={(e) => { e.stopPropagation(); changePoolStatus(candidate.candidate_id, s.key); }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-gray-50 ${candidate.pool_status === s.key ? 'bg-blue-50 font-medium' : ''}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              );
            })}
            <div className="border-t mt-1 pt-1">
              <button onClick={(e) => { e.stopPropagation(); changePoolStatus(candidate.candidate_id, 'removed'); }}
                className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Remove From Pool
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // === COLOR DOT COMPONENT ===
  const ColorDot = ({ candidate }) => {
    const isOpen = openColorPicker === candidate.candidate_id;
    const currentColor = candidate.color ? (COLOR_MAP[candidate.color] || candidate.color) : null;

    return (
      <div className="relative pool-color-picker">
        <button
          onClick={(e) => { e.stopPropagation(); setOpenColorPicker(isOpen ? null : candidate.candidate_id); setOpenStatusDropdown(null); }}
          className="w-5 h-5 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-gray-400 transition-colors"
          title={candidate.color ? `Color: ${candidate.color}` : 'Set color'}
        >
          {currentColor ? (
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: currentColor }} />
          ) : (
            <span className="w-3 h-3 rounded-full bg-gray-100" />
          )}
        </button>
        {isOpen && (
          <div className="absolute z-50 right-0 top-full mt-1 bg-white border rounded-lg shadow-xl p-2 flex gap-1.5 flex-wrap w-24">
            {PRESET_COLORS.map(c => (
              <button key={c.key} onClick={(e) => { e.stopPropagation(); changeColor(candidate.candidate_id, c.key); }}
                className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${candidate.color === c.key ? 'border-gray-800 ring-1 ring-gray-400' : 'border-transparent'}`}
                style={{ backgroundColor: c.hex }} title={c.key}
              />
            ))}
            <button onClick={(e) => { e.stopPropagation(); changeColor(candidate.candidate_id, null); }}
              className="w-5 h-5 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-gray-500"
              title="Clear color">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const searchCode = `SR-${String(pipelineId).padStart(4, '0')}`;

  if (loading) return <AppShell variant="recruiter"><div className="p-8 text-center text-gray-500">Loading search...</div></AppShell>;

  // Pass 1 (design refresh): the top-level tabs moved from the vertical
  // mini-sidebar into a horizontal AppShell pageStrip. Requirements
  // sits on the right of the pageStrip as a distinct toggle button
  // (not a tab — it opens a slide-down panel over whatever tab you're
  // on). Behavior is identical to the old mini-sidebar; only the layout
  // and design tokens change.
  //
  // Iteration: icons added to all nav entries. Sub-tab IDs unchanged
  // (`kanban`, `client-stages`) since they're used in state, URL
  // params, and PipelineBoard's activeView prop — only the display
  // labels changed to "Presentation" and "Interview Stages".
  const TOP_TABS = [
    { id: 'research', label: 'Mapping', iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
    { id: 'pool',     label: 'Pool',     iconPath: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
    { id: 'outreach', label: 'Outreach', iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { id: 'pipeline', label: 'Pipeline', iconPath: 'M4 6h16M4 12h16M4 18h7' },
  ];

  // Sub-tabs render only when Pipeline is active. Rendered as tool
  // buttons (grey palette, icon+label, shadow-inner on press) rather
  // than emerald pills — the "tool" aesthetic matches the user's
  // ask that they feel more like a traditional pressed button than a
  // navigation tab.
  //
  // Placed is intentionally omitted here: it happens once or a few
  // times per search, so it doesn't earn a permanent tab. It surfaces
  // as a chip in the search context bar; clicking the chip jumps into
  // the placed view (state still supports it).
  const PIPELINE_SUBTABS = [
    { id: 'kanban',        label: 'Presentation',    iconPath: 'M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25' },
    { id: 'client-stages', label: 'Interview Stages', iconPath: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
    { id: 'status-report', label: 'Status Report',   iconPath: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { id: 'disqualified',  label: 'Disqualified',    iconPath: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' },
  ];

  const tabStrip = (
    <div className="bg-white px-3 h-[40px] flex items-center">
      <nav className="flex gap-1 h-[40px] -mb-px items-stretch flex-shrink-0">
        {TOP_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 text-xs font-medium border-b-2 rounded-none transition-colors ${
              activeTab === t.id
                ? 'border-emerald-600 text-emerald-700 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.iconPath} />
            </svg>
            {t.label}
          </button>
        ))}
      </nav>

      {/* Search context — moved onto the tab row, after the last tab,
          with a border separator (matches the /candidates query bar). */}
      <div className="flex items-center gap-2 min-w-0 h-6 pl-3 ml-1 border-l border-gray-200">
        <h1 className="text-sm font-semibold text-gray-900 truncate min-w-0">{pipeline?.name || `Search #${pipelineId}`}</h1>
        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-mono flex-shrink-0">{searchCode}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
          pipeline?.status === 'active' ? 'bg-green-100 text-green-700' :
          pipeline?.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
          'bg-gray-100 text-gray-700'
        }`}>{pipeline?.status}</span>
        <span className="text-[10px] text-gray-400 truncate">{pipeline?.firm_name || ''}</span>
        <button
          onClick={() => setShowEditDrawer(true)}
          title="Edit search"
          className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
        </button>
      </div>

      <div className="flex-1" />

      {/* Counts stay on the right */}
      <div className="flex items-center gap-3 text-[11px] text-gray-400 flex-shrink-0 tabular-nums">
        <span><span className="font-medium text-gray-600">{counts.total}</span> pool</span>
        {counts.on_pipeline > 0 && <span><span className="font-medium text-blue-600">{counts.on_pipeline}</span> pipeline</span>}
        {(statusCounts.placed || 0) > 0 && (
          <button
            onClick={() => { setActiveTab('pipeline'); setPipelineSubTab('placed'); }}
            title="View placed candidates"
            className="flex items-center gap-1 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100 transition-colors flex-shrink-0"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            {statusCounts.placed} placed
          </button>
        )}
      </div>

      {/* Requirements is a slide-down panel toggle, not a tab. Kept
          visually distinct as a pill. */}
      <button
        onClick={() => setShowRequirements(!showRequirements)}
        className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
          showRequirements
            ? 'bg-emerald-100 text-emerald-800'
            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
        }`}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Requirements
      </button>
    </div>
  );

  // When candidates are selected, the bulk bar REPLACES the tab strip in the
  // same row (matches Firms/Searches/Outreach). CandidateTable's own bulk bar
  // is suppressed (suppressBulkBar) so it doesn't double up.
  const bulkStrip = (
    <BulkActionBar
      count={selectedIds.length}
      onClear={() => { setSelectedIds([]); setClearTrigger(t => t + 1); }}
    >
      <select onChange={(e) => { if (e.target.value) { bulkChangeStatus(e.target.value); e.target.value = ''; } }}
        className="text-xs px-2 h-[26px] rounded bg-white/10 text-white border border-white/30 hover:bg-white/15 focus:outline-none focus:ring-1 focus:ring-white/40 transition-colors [&>option]:text-gray-800">
        <option value="">Change status…</option>
        {POOL_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        <option value="removed">Remove from pool</option>
      </select>
      <div className="flex items-center gap-1 pl-1">
        <span className="text-[10px] text-white/70">Color</span>
        {PRESET_COLORS.map(c => (
          <button key={c.key} onClick={() => bulkChangeColor(c.key)}
            className="w-4 h-4 rounded-full border border-white/40 hover:scale-125 transition-transform"
            style={{ backgroundColor: c.hex }} title={c.key} />
        ))}
        <button onClick={() => bulkChangeColor(null)} className="w-4 h-4 rounded-full border border-dashed border-white/50 flex items-center justify-center hover:border-white" title="Clear color">
          <svg className="w-2.5 h-2.5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <button onClick={() => openCampaignSidebar(selectedIds)}
        className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15 flex items-center gap-1">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
        Campaign
      </button>
      <button onClick={() => promoteToIPipeline(pipelineId, selectedIds)}
        className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15 flex items-center gap-1">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        Move to Pipeline
      </button>
      <button onClick={exportSelectedCsv}
        className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15 flex items-center gap-1">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Export CSV
      </button>
    </BulkActionBar>
  );

  // Mapping tab bulk bar — lifted from ResearchTab so it overlays the tab nav
  // in the pageStrip like every other page's bulk bar.
  const mappingBulkStrip = mappingBulk ? (
    <BulkActionBar count={mappingBulk.count} onClear={mappingBulk.clear}>
      <span className="text-[10px] text-white/70">Status</span>
      {mappingBulk.statusOptions.map(s => (
        <button key={s.value} onClick={() => mappingBulk.setStatus(s.value)}
          className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15">{s.label}</button>
      ))}
      <div className="h-4 w-px bg-white/25 mx-1" />
      <span className="text-[10px] text-white/70">Priority</span>
      {mappingBulk.priorityOptions.map(p => (
        <button key={p.key} onClick={() => mappingBulk.setPriority(p.key)}
          className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15">{p.label}</button>
      ))}
      <button onClick={mappingBulk.clearPriority}
        className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15">Clear</button>
      <div className="h-4 w-px bg-white/25 mx-1" />
      <button onClick={mappingBulk.remove}
        className="text-xs px-2.5 py-1 rounded transition-colors text-white/90 hover:bg-white/15">Delete</button>
    </BulkActionBar>
  ) : null;

  const pageStrip = (activeTab === 'research' && mappingBulkStrip)
    ? mappingBulkStrip
    : (selectedIds.length > 0 ? bulkStrip : tabStrip);

  return (
    <AppShell
      variant="recruiter"
      pageStrip={pageStrip}
      rightPanel={showEditDrawer ? (
        <EditSearchDrawer
          pipeline={pipeline}
          onClose={() => setShowEditDrawer(false)}
          onSaved={(updated) => setPipeline(prev => ({ ...prev, ...updated }))}
        />
      ) : null}
    >
      <div className="h-full flex flex-col overflow-hidden">

      {activeTab === 'research' && (
        <ResearchTab pipelineId={pipelineId} searchName={pipeline?.name} onBulkContext={setMappingBulk} />
      )}

      {/* Pipeline sub-tabs — tool button aesthetic. White base sitting
          up on shadow-sm; gray-200 background with inset shadow when
          pressed. Icon color is emerald-600 across all four tabs to
          give the strip a unified brand feel (no per-outcome red/green
          swap — the button chrome carries the pressed state instead).
          Right side hosts the Manage Stages button — only visible when
          Presentation is active (since it manages the kanban stages).
          Same tool-button aesthetic; dispatches banyak:open_stage_manager
          for PipelineBoard to catch. */}
      {activeTab === 'pipeline' && (
        <div className="bg-gray-50 border-b px-4 py-2 flex items-center gap-1.5 flex-shrink-0 overflow-x-auto">
          {PIPELINE_SUBTABS.map(s => {
            const isActive = pipelineSubTab === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setPipelineSubTab(s.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  isActive
                    ? 'bg-gray-200 text-gray-900 border-gray-300 shadow-inner'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm'
                }`}
              >
                <svg className={`w-3.5 h-3.5 transition-colors ${isActive ? 'text-emerald-700' : 'text-emerald-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={s.iconPath} />
                </svg>
                {s.label}
              </button>
            );
          })}
          <div className="flex-1" />
          {(pipelineSubTab === 'kanban' || pipelineSubTab === 'client-stages') && (
            <ClientAlertsToggle pipelineId={pipelineId} />
          )}
          {(pipelineSubTab === 'kanban' || pipelineSubTab === 'client-stages') && (
            <button
              onClick={() => {
                // Different sub-tabs manage different stage systems.
                // PipelineBoard listens for both events and opens the
                // appropriate manager modal.
                const evt = pipelineSubTab === 'kanban'
                  ? 'banyak:open_stage_manager'
                  : 'banyak:open_client_stage_manager';
                window.dispatchEvent(new CustomEvent(evt));
              }}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm transition-colors"
            >
              <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Manage Stages
            </button>
          )}
        </div>
      )}

      {/* REQUIREMENTS PANEL */}
      {showRequirements && (
        <div className="border-b bg-white overflow-auto" style={{ maxHeight: '40vh' }}>
        <div className="p-4 space-y-4 max-w-4xl">
          {pipeline?.location && (
            <div className="flex gap-2 text-sm"><span className="font-medium text-gray-600 w-24 flex-shrink-0">Location:</span><span className="text-gray-800">{pipeline.location}</span></div>
          )}
          {pipeline?.compensation_range && (
            <div className="flex gap-2 text-sm"><span className="font-medium text-gray-600 w-24 flex-shrink-0">Comp Range:</span><span className="text-gray-800">{pipeline.compensation_range}</span></div>
          )}
          {pipeline?.description && (
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-6 py-4 border-b bg-gray-50 rounded-t-lg"><h2 className="text-lg font-semibold text-gray-900">Description</h2></div>
              <div className="p-6"><p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{pipeline.description}</p></div>
            </div>
          )}
          {pipeline?.requirements && (
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-6 py-4 border-b bg-gray-50 rounded-t-lg"><h2 className="text-lg font-semibold text-gray-900">Requirements</h2></div>
              <div className="p-6"><div className="text-gray-700 whitespace-pre-wrap leading-relaxed">{pipeline.requirements}</div></div>
            </div>
          )}
          {pipeline?.job_description_file_url && (
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-6 py-4 border-b bg-gray-50 rounded-t-lg"><h2 className="text-lg font-semibold text-gray-900">Attached Job Description</h2></div>
              <div className="p-6">
                <a href={pipeline.job_description_file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  <span className="font-medium">{pipeline.job_description_file_name || 'Download Job Description'}</span>
                </a>
              </div>
            </div>
          )}
          {!pipeline?.description && !pipeline?.requirements && !pipeline?.job_description_file_url && !pipeline?.location && !pipeline?.compensation_range && (
            <div className="bg-white rounded-lg border shadow-sm p-12 text-center">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <p className="text-gray-500 font-medium">No search requirements provided yet</p>
              <p className="text-sm text-gray-400 mt-1">The client will fill in requirements when creating or editing the search.</p>
            </div>
          )}
        </div>
        </div>
      )}

      {/* SEARCH POOL */}
      {activeTab === 'pool' && (
        <div className="flex-1 flex flex-col overflow-hidden">

          <CandidateTable
            candidates={candidates}
            loading={candidatesLoading}
            onFiltersChange={loadCandidates}
            filterOptions={{ referral_sources: [], tags: [], locations: [] }}
            pipelines={[]}
            mode="recruiter"
            embedded={true}
            showPipelineFilter={false}
            showQuickFilters={true}
            showTagFilter={false}
            showSavedViews={false}
            toolbarExtra={
              <div className="flex items-center gap-1.5">
                <FilterSelect
                  label="Status"
                  value={poolStatusFilter || 'all'}
                  defaultValue="all"
                  onChange={(v) => setPoolStatusFilter(v === 'all' ? '' : v)}
                  options={[
                    { value: 'all', label: `All statuses (${counts.total})` },
                    ...POOL_STATUSES.filter(s => statusCounts[s.key]).map(s => ({ value: s.key, label: `${s.label} (${statusCounts[s.key]})` })),
                  ]}
                />
                <FilterSelect
                  label="Color"
                  value={colorFilter || 'all'}
                  defaultValue="all"
                  onChange={(v) => setColorFilter(v === 'all' ? '' : v)}
                  options={[
                    { value: 'all', label: 'All colors' },
                    ...PRESET_COLORS.map(c => ({ value: c.key, label: c.key })),
                    { value: 'none', label: 'No color' },
                  ]}
                />
              </div>
            }
            showAddButton={true}
            onAddButtonClick={() => { setShowAddModal(true); setAddSearch(''); setAddResults([]); }}
            onBulkAddToPipeline={promoteToIPipeline}
            onSelectionChange={setSelectedIds}
            suppressBulkBar={true}
            clearSelectionTrigger={clearTrigger}
            contextPipelineId={pipelineId}
            exportLabel={pipeline?.name || 'search-pool'}
            rowAfterName={(candidate) => {
              const isTerminal = TERMINAL_STATUSES.includes(candidate.pool_status);
              return (
                <div className={`flex items-center gap-1.5 flex-shrink-0 ${isTerminal ? 'opacity-50' : ''}`}>
                  <ColorDot candidate={candidate} />
                  <StatusPill candidate={candidate} />
                </div>
              );
            }}
            rowActions={(candidate) => (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openCampaignSidebar([candidate.candidate_id])}
                  className="px-2 py-0.5 text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded hover:bg-purple-100"
                  title="Add to Campaign"
                >Campaign</button>
                <button
                  onClick={() => handlePromoteSingle(candidate.candidate_id)}
                  className="px-2 py-0.5 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100"
                  title="Add to Pipeline"
                >Pipeline</button>
              </div>
            )}
          />
        </div>
      )}

      {/* OUTREACH */}
      {activeTab === 'outreach' && (
        <SearchOutreachTab pipelineId={pipelineId} pipelineName={pipeline?.name} />
      )}

      {/* PIPELINE VIEWS */}
      {activeTab === 'pipeline' && ['kanban', 'client-stages', 'disqualified', 'placed'].includes(pipelineSubTab) && (
        <PipelineBoard pipelineId={pipelineId} activeView={pipelineSubTab} hideManageStagesButton />
      )}

      {activeTab === 'pipeline' && pipelineSubTab === 'status-report' && (
        <StatusReport pipelineId={pipelineId} pipelineName={pipeline?.name} />
      )}

      </div>{/* close h-full flex wrapper */}

      {/* Add Candidates Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Add Candidates to Search</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-4">
              <input ref={addSearchRef} type="text" value={addSearch} onChange={(e) => setAddSearch(e.target.value)} autoFocus
                placeholder="Search by name, title, or company..." className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
              {addSearching && <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div></div>}
              {!addSearching && addSearch.length >= 2 && addResults.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No candidates found for "{addSearch}"</p>
              )}
              {addResults.map(c => (
                <div key={c.candidate_id} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    {c.profile_picture_url ? (
                      <img src={c.profile_picture_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium flex-shrink-0">
                        {c.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{c.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {c.current_title || 'No title'}{c.current_company ? ` at ${c.current_company}` : ''}
                      </div>
                    </div>
                  </div>
                  {c.already_in_search ? (
                    <span className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded-full flex-shrink-0">Already added</span>
                  ) : (
                    <button onClick={() => { addToSearch([c.candidate_id]); setAddResults(prev => prev.map(r => r.candidate_id === c.candidate_id ? { ...r, already_in_search: true } : r)); }}
                      className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-shrink-0">
                      Add
                    </button>
                  )}
                </div>
              ))}
              {!addSearch && (
                <p className="text-sm text-gray-400 text-center py-6">Type at least 2 characters to search your candidate database</p>
              )}
            </div>
            <div className="px-4 py-3 border-t flex justify-end">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border text-sm rounded-lg hover:bg-gray-50">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Campaign — shared sidebar rendered as a fixed-position
          right-side overlay. Same component CandidatePreviewPanel uses
          in its left panel column; here it slides in over the page. */}
      {campaignSidebarCandidates && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={() => setCampaignSidebarCandidates(null)}
          />
          <div className="fixed right-0 top-0 bottom-0 w-96 bg-white shadow-2xl border-l z-50 flex flex-col overflow-hidden">
            <AddToCampaignSidebar
              candidateIds={campaignSidebarCandidates}
              pipelineId={pipelineId}
              onClose={() => setCampaignSidebarCandidates(null)}
              onEnrolled={(result) => {
                const msgs = [];
                if (result?.enrolled > 0) msgs.push(`${result.enrolled} enrolled`);
                if (result?.skipped > 0) msgs.push(`${result.skipped} already enrolled`);
                if (msgs.length) alert(msgs.join(', '));
                setSelectedIds([]);
                setCampaignSidebarCandidates(null);
              }}
            />
          </div>
        </>
      )}
    </AppShell>
  );
};

export default SearchDetailPage;
